/**
 * Component: Bulk Order Progress
 * -----------------------------------------------------------------------------
 * Logic related to PDP Bulk Order Progress bar.
 * @namespace BulkOrderProgress
 */
import classes from '~/config/classes'

/**
 * DOM selectors.
 */
const selectors = {
  wrapper: '[js-bulk-progress="wrapper"]',
  content: '[js-bulk-progress="content"]',
  progress: '[js-bulk-progress="progress"]',
  modalLink: '[js-bulk-progress="modal-link"]',
  cartMessage: '[js-bulk-progress="cartMessage"]',
  cartItem: '[data-cart-item]',
  cartItemPrice: '[data-cart-item-price]',
}

/**
 * Create a new BulkOrderProgress object.
 */
export default () => {

  /**
   * Component State.
   */
  const state = {
    cart: {},
    discounts: {
      sorted: {},
      reversed: {},
      active: {},
    },
    itemCount: 0,
    qualify: false,
  }

  /**
   * Node Elements.
   */
  const nodes = {
    wrapper: document.querySelector(selectors.wrapper),
    content: document.querySelector(selectors.content),
    progress: document.querySelector(selectors.progress),
    modalLink: document.querySelector(selectors.modalLink),
    cartMessage: document.querySelector(selectors.cartMessage),
  }

  /**
   * Initialize component.
   */
  function init() {
    if (!checkActive()) {
      nodes.wrapper.remove()
      return
    }

    if (!nodes.modalLink) {
      return
    }

    nodes.modalLink.setAttribute('href', window.bulkOrderProgress.popup.link)

    setEventBusListeners()
    sortDiscounts()
    runProgress()
  }

  /**
   * Listen for basis.EventBus events.
   */
  function setEventBusListeners() {
    window.basis.EventBus.listen('Cart:render:full', () => {
      runProgress()
    })

    window.basis.EventBus.listen('Cart:render:empty', () => {
      nodes.wrapper.classList.add(classes.hidden)
    })
  }

  /**
   * Update state and render UI.
   */
  function runProgress() {
    getCart().then(() => {
      const { popup, visibilityThreshold } = window.bulkOrderProgress

      checkQualification()

      if (state.itemCount >= popup.threshold) {
        openModal()
      }

      if (
        visibilityThreshold > 0 &&
        state.itemCount < visibilityThreshold
      ) {
        nodes.wrapper.classList.add(classes.hidden)
      } else {
        nodes.wrapper.classList.remove(classes.hidden)
      }

      render()
    })
  }

  /**
   * Check if UI should be active.
   */
  function checkActive() {
    const { enable, discounts, type } = window.bulkOrderProgress

    return (
      enable &&
      discounts?.length &&
      type
    )
  }

  /**
   * Render UI
   */
  function render() {
    const config = window.bulkOrderProgress
    const ready = state.qualify && state.discounts.active
    const dictionary = ready
      ? {
        x: state.discounts.active.threshold,
        y: state.discounts.active.discount,
      }
      : {
        x: state.discounts.sorted[0].threshold - state.itemCount,
        y: state.discounts.sorted[0].discount,
      }
    let content = ready
      ? translateContent(config.postQualifyContent, dictionary)
      : translateContent(config.preQualifyContent, dictionary)

    if (!ready && dictionary.x === 1 && content.includes('pairs')) {
      content = content.replace('pairs', 'pair')
    }

    if (!ready && dictionary.x > 1 && content.includes('pair')) {
      content = content.replace('pair', 'pairs')
    }

    const progress = ready ? state.discounts.active.threshold : state.itemCount
    const max = ready ? state.discounts.active.threshold : state.discounts.sorted[0].threshold
    const percent = (progress / max) * 100

    nodes.content.innerHTML = content
    nodes.progress.setAttribute('value', progress)
    nodes.progress.setAttribute('max', max)
    nodes.progress.setAttribute('aria-label', `${percent}%`)
    nodes.wrapper.classList.remove(classes.loading)

    if (ready) {
      hideCartMessage()
      updateSalePrice()
    } else {
      showCartMessage()
    }
  }

  /**
   * Hide Cart Message container when line item discount is applied.
   */
  function hideCartMessage() {
    if (!nodes.cartMessage) {
      return
    }

    nodes.cartMessage.classList.add(classes.hidden)
  }

  /**
   * Show Cart Message.
   */
  function showCartMessage() {
    if (!nodes.cartMessage) {
      return
    }

    nodes.cartMessage.classList.remove(classes.hidden)
  }

  /**
   * Update sale price for each line item in cart drawer.
   */
  function updateSalePrice() {
    const items = document.querySelectorAll(selectors.cartItem)
    const typeArray = window.bulkOrderProgress.type.split(',')
    const typeArrayClean = typeArray.map((type) => {
      return type.toLowerCase().trim()
    })

    items.forEach((item) => {
      const productType = item.dataset.productType.toLowerCase()

      if (!typeArrayClean.includes(productType)) {
        return
      }

      const id = item.getAttribute('data-cart-item')
      const priceEl = item.querySelector(selectors.cartItemPrice)
      const discountedItem = state.cart.items.find((cartItem) => cartItem.id.toString() === id)

      if (!discountedItem) {
        return
      }

      const { price } = discountedItem
      const discount = price * (state.discounts.active.discount / 100)
      const oldPrice = (price / 100).toFixed(2)
      const newPrice = ((price - discount) / 100).toFixed(2)

      priceEl.innerHTML = `<del>$${oldPrice}</del> <mark>$${newPrice}</mark>`
    })
  }

  /**
   * Translate Content.
   * @param {String} content
   * @returns {String}
   */
  function translateContent(content, dictionary) {
    return content
      .replace('{x}', dictionary.x)
      .replace('{y}', dictionary.y)
  }

  /**
   * Sort discounts by threshold.
   */
  function sortDiscounts() {
    const discounts = [...window.bulkOrderProgress.discounts]
    const reversed = [...window.bulkOrderProgress.discounts]

    if (!discounts) {
      return
    }

    state.discounts.sorted = discounts.sort((a, b) => a.threshold - b.threshold)
    state.discounts.reversed = reversed.sort((a, b) => b.threshold - a.threshold)
  }

  /**
   * Get Cart
   */
  async function getCart() {
    await fetch('/cart.js')
      .then((response) => response.json())
      .then((data) => {
        state.cart = data
      })
  }

  /**
   * Check cart contents to determine if it qualifies for bulk discount.
   */
  function checkQualification() {
    const { cart } = state
    const discounts = state.discounts?.sorted

    if (!discounts?.length || !cart.items.length) {
      state.qualify = false
      return
    }

    const items = getQualifyingItems()

    state.itemCount = 0

    items.forEach((item) => {
      state.itemCount += item.quantity
    })

    if (state.itemCount < discounts[0].threshold) {
      state.qualify = false
      return
    }

    state.qualify = true

    state.discounts.active = state.discounts.reversed.find((discount) => {
      return state.itemCount >= discount.threshold
    })
  }

  /**
   * Get Qualifying items from cart.
   * @returns {Array}
   */
  function getQualifyingItems() {
    const { cart } = state
    const typeArray = window.bulkOrderProgress.type.split(',')
    const typeArrayClean = typeArray.map((type) => {
      return type.toLowerCase().trim()
    })

    const items = cart.items.filter((item) => {
      const productType = item.product_type

      return typeArrayClean.includes(productType?.toLowerCase())
    })

    return items
  }

  /**
   * Open bulk orders modal.
   */
  function openModal() {
    if (localStorage.getItem('Stoggles_BulkModalClosed')) {
      return
    }

    // eslint-disable-next-line no-undef
    MicroModal.show('BulkOrderModal')
    localStorage.setItem('Stoggles_BulkModalClosed', true)
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
